/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, Grid, Hidden, PageGridContainer, Typography, Typography2, breakpoints } from '@coursera/cds-core';

import LazyImgix from 'bundles/page/components/shared/LazyImgix';
import Section from 'bundles/unified-description-page-common/components/shared/Section';
import Modal, { useModal } from 'bundles/unified-description-page-common/components/shared/modals/Modal';

import _t from 'i18n!nls/xdp';

const IMAGE_SIZE = 80;

type Props = {
  title: string;
  text: string;
  imageSrc?: string;
};

const styles = {
  container: () => css`
    padding: var(--cds-spacing-400);
    background: var(--cds-color-grey-25);
    border-radius: 8px;
  `,
  linkButton: () => css`
    padding: 0;
    margin: var(--cds-spacing-200) 0 0 0;
    height: unset;
    width: unset;

    :hover {
      background: none;
      box-shadow: none;
      border: none;
    }
  `,
  content: () => css`
    display: flex;
    gap: var(--cds-spacing-200);
  `,
  image: css`
    height: ${IMAGE_SIZE}px;
  `,
  imageContainer: () => css`
    display: flex;
    align-items: flex-start;
    justify-content: end;
    ${breakpoints.down('sm')} {
      display: none;
    }
  `,
};

const LearnerIncentiveModule = ({ title, text, imageSrc }: Props) => {
  const { isModalOpen, setModalOpen } = useModal();

  if (!title) {
    return null;
  }

  const image = imageSrc && (
    <div css={styles.image}>
      <LazyImgix src={imageSrc} alt="" maxHeight={IMAGE_SIZE} maxWidth={IMAGE_SIZE} />
    </div>
  );

  return (
    <Section>
      <PageGridContainer>
        <Grid item xs={12}>
          <div css={styles.container}>
            <Grid container>
              <Grid item sm={12} md={7} css={styles.content}>
                <div>
                  <Typography variant="h1semibold" component="h2">
                    {title}
                  </Typography>
                  {text && (
                    <React.Fragment>
                      <Button
                        variant="ghost"
                        css={styles.linkButton}
                        onClick={setModalOpen}
                        aria-label={_t('Learn more about #{title}', { title })}
                      >
                        {_t('Learn more')}
                      </Button>
                      <Modal header={_t('Promotion details')} open={isModalOpen} setOpen={setModalOpen}>
                        <Typography2 component="p" variant="bodyPrimary">
                          {text}
                        </Typography2>
                      </Modal>
                    </React.Fragment>
                  )}
                </div>
                <Hidden mdUp>{image}</Hidden>
              </Grid>
              <Grid item sm={12} md={5} css={styles.imageContainer}>
                {image}
              </Grid>
            </Grid>
          </div>
        </Grid>
      </PageGridContainer>
    </Section>
  );
};

export default LearnerIncentiveModule;
